import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { accentBtnSx } from "../../../colors";

export default function ActionActualizareStatus({
  status,
  setStatus,
  isStatusSolved,
  formActionsData,
  handleUpdateStatus,
  currentStatus,
  additionalInfoMessage,
  setAdditionalInfoMessage
}) {
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  // Check if selected status requires additional info
  useEffect(() => {
    if (formActionsData?.status_choices) {
      const selectedStatus = formActionsData.status_choices.find(opt => opt.value === status);
      // Check if status is "Necesită informații suplimentare" or "ADDITIONAL_INFO_REQUIRED"
      const requiresInfo = selectedStatus?.label === "Necesită informații suplimentare" || 
                          selectedStatus?.value === "ADDITIONAL_INFO_REQUIRED" ||
                          status === "ADDITIONAL_INFO_REQUIRED";
      setShowAdditionalInfo(requiresInfo);
      
      // Clear message if not required
      if (!requiresInfo && setAdditionalInfoMessage) {
        setAdditionalInfoMessage("");
      }
    }
  }, [status, formActionsData, setAdditionalInfoMessage]);

  // Helper to get value from label or value
  const getStatusValue = (input) => {
    if (!formActionsData?.status_choices) return input;
    const found = formActionsData.status_choices.find(
      (opt) => opt.value === input || opt.label === input
    );
    return found ? found.value : input;
  };

  const isSameStatus = getStatusValue(status) === getStatusValue(currentStatus);

  const handleConfirmUpdateStatus = async () => {
    // Prevent update if status is unchanged (compare with backend status)
    if (isSameStatus) {
      toast.error("Statusul este deja setat la această valoare.");
      return;
    }
    if (typeof handleUpdateStatus === "function") {
      const result = await Swal.fire({
        title: "Ești sigur?",
        text: "Vrei să actualizezi statusul sesizării?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Da, actualizează!",
        cancelButtonText: "Anulează"
      });
      if (result.isConfirmed) {
        handleUpdateStatus(additionalInfoMessage);
      }
    }
  };

  return (
    <SoftBox 
      p={2.5}
      border="1px solid #eee"
      borderRadius="lg"
      boxShadow="0 2px 12px 0 rgba(0,0,0,0.05)"
      bgcolor="white"
      position="relative"
      overflow="hidden"
    >
      <SoftBox 
        position="absolute" 
        top={0} 
        left={0} 
        width="4px" 
        height="100%" 
        bgcolor="info.main" 
      />
      <SoftTypography variant="subtitle2" fontWeight="medium" mb={2} pl={1}>
        <Icon fontSize="small" sx={{ verticalAlign: "middle", mr: 1 }}>update</Icon>
        Actualizare Status
      </SoftTypography>
      <TextField
        select
        fullWidth
        value={status}
        onChange={handleStatusChange}
        disabled={!!isStatusSolved}
        size="small"
        sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        SelectProps={{
          renderValue: (selected) => {
            const selectedLabel = formActionsData?.status_choices?.find(opt => opt.value === selected)?.label || selected;
            return (
              <SoftBox display="flex" alignItems="center">
                <Icon fontSize="small" sx={{ mr: 1 }}>
                  check_circle
                </Icon>
                {selectedLabel}
              </SoftBox>
            );
          }
        }}
      >
        {formActionsData?.status_choices?.length
          ? formActionsData.status_choices.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} disabled={opt.value === currentStatus}>
                <SoftBox display="flex" alignItems="center">
                  <Icon fontSize="small" sx={{ mr: 1 }}>check_circle</Icon>
                  {opt.label}
                </SoftBox>
              </MenuItem>
            ))
          : <MenuItem value="" disabled>Nu există opțiuni</MenuItem>
        }
      </TextField>

      {showAdditionalInfo && (
        <SoftBox mb={1.5}>
          <SoftTypography variant="caption" fontWeight="medium" mb={0.5} pl={0.5} display="block" sx={{ color: '#475569' }}>
            Specificați ce informații suplimentare sunt necesare:
          </SoftTypography>
          <textarea
            style={{
              width: "100%",
              minHeight: 60,
              height: 100,
              borderRadius: 8,
              border: "1px solid #ccc",
              padding: 8,
              fontFamily: "inherit",
              fontSize: "0.875rem",
              resize: "vertical",
              backgroundColor: "#f8fafc",
            }}
            placeholder="Ex: Vă rugăm să furnizați documente care să ateste identitatea dumneavoastră..."
            value={additionalInfoMessage || ""}
            onChange={(e) => setAdditionalInfoMessage && setAdditionalInfoMessage(e.target.value)}
            disabled={!!isStatusSolved}
          />
          <SoftTypography variant="caption" sx={{ color: '#64748b', mt: 0.5, display: 'block' }}>
            Acest mesaj va fi vizibil pentru utilizator în pagina de verificare sesizare.
          </SoftTypography>
        </SoftBox>
      )}

      <SoftButton 
        variant="gradient" 
        color="info" 
        size="small"
        disabled={!!isStatusSolved || isSameStatus}
        fullWidth
        sx={{ ...accentBtnSx }}
        onClick={handleConfirmUpdateStatus}
      >
        <Icon fontSize="small" sx={{ mr: 0.5 }}>check_circle</Icon>
        Actualizează status
      </SoftButton>
    </SoftBox>
  );
}

ActionActualizareStatus.propTypes = {
  status: PropTypes.string.isRequired,
  setStatus: PropTypes.func.isRequired,
  isStatusSolved: PropTypes.bool.isRequired,
  formActionsData: PropTypes.object,
  handleUpdateStatus: PropTypes.func.isRequired,
  currentStatus: PropTypes.string.isRequired,
  additionalInfoMessage: PropTypes.string,
  setAdditionalInfoMessage: PropTypes.func,
};
